import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import type { InventoryCategory, CategoryCreatePayload, CategoryUpdatePayload } from '../types/categories.types';

export const categoriesService = {
  async fetchCategories(companyId: string) {
    if (!companyId) return [];

    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('company_id', companyId)
      .eq('group_id', 'e9d39119-c221-4464-a3ab-80f90e2e416a')
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []) as InventoryCategory[];
  },

  async createCategory(userId: string, payload: CategoryCreatePayload) {
    await permissionEngine.assert(userId, 'inventory.categories.create', { companyId: payload.company_id });

    // Handle path and level logic if parent_id is set
    let level = 1;
    let path = null;
    if (payload.parent_id) {
      const { data: parent } = await supabase
        .from('categories')
        .select('level, path')
        .eq('id', payload.parent_id)
        .single();
      if (parent) {
        level = (parent.level || 0) + 1;
        path = parent.path ? `${parent.path}.${payload.parent_id}` : payload.parent_id;
      }
    }

    const { data, error } = await supabase
      .from('categories')
      .insert({
        ...payload,
        group_id: 'e9d39119-c221-4464-a3ab-80f90e2e416a',
        level,
        path,
        is_active: payload.is_active ?? true,
        display_order: payload.display_order || 0,
      })
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  },

  async updateCategory(userId: string, companyId: string, id: string, payload: CategoryUpdatePayload) {
    await permissionEngine.assert(userId, 'inventory.categories.update', { companyId });

    // Path logic would normally need a recursive trigger or complex logic to update children.
    // For MVP, we simply update the current item. If parent changes, re-calculate level.
    let updateData = { ...payload };
    
    if ('parent_id' in payload) {
      if (payload.parent_id) {
        const { data: parent } = await supabase
          .from('categories')
          .select('level, path')
          .eq('id', payload.parent_id)
          .single();
        if (parent) {
          updateData.level = (parent.level || 0) + 1;
          updateData.path = parent.path ? `${parent.path}.${payload.parent_id}` : payload.parent_id;
        }
      } else {
        updateData.level = 1;
        updateData.path = null;
      }
    }

    const { error } = await supabase
      .from('categories')
      .update(updateData)
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
    return true;
  },

  async deleteCategory(userId: string, companyId: string, id: string) {
    await permissionEngine.assert(userId, 'inventory.categories.delete', { companyId });

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
    return true;
  },
};
